import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../api/api';
import { useOrg } from '../orgs/org-context';
import { printStatus } from './printAccountingApi';

// Only assignment DTOs enter this view; legacy DataDoc counters are never inputs.
export default function PrintAccounting({ dataDocId, revision = 0, autoRefresh = true, uncertainJobId }) {
  const { activeOrgId } = useOrg();
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState({});
  useEffect(() => {
    let active = true;
    let busy = false;
    setState({});
    async function load() {
      if (busy) return;
      busy = true;
      try {
        const base = `/api/printmanager/datadocs/${encodeURIComponent(dataDocId)}`;
        const [summary, history] = await Promise.all([
          apiFetch(`${base}/print-summary`, { orgIdOverride: activeOrgId }),
          apiFetch(`${base}/print-history?limit=20`, { orgIdOverride: activeOrgId })
        ]);
        if (active) setState({ dataDocId, activeOrgId, summary, history });
      } catch {
        if (active) setState({ dataDocId, activeOrgId, error: true });
      } finally { busy = false; }
    }
    load();
    const timer = autoRefresh ? setInterval(load, 5000) : null;
    return () => { active = false; clearInterval(timer); };
  }, [dataDocId, revision, refresh, activeOrgId, autoRefresh]);
  const current = state.dataDocId === dataDocId && state.activeOrgId === activeOrgId ? state : {};
  const s = current.summary;
  return <section aria-label="Print history" style={{ padding: 12 }}>
    <strong>Print requests</strong>{' '}
    <button type="button" onClick={() => setRefresh(v => v + 1)}>Refresh history</button>
    {current.error ? <p role="alert">Print history unavailable for this record in the current organization. Refresh to try again.</p>
      : !s ? <p>Loading print history...</p> : <>
        <p>{s.admittedRequestCount} requests accepted - {s.requestedCopyCount} requested copies{s.requestedCopyCountUnknown ? ' (additional copies unknown)' : ''}</p>
        {uncertainJobId && <p role="status">{current.history.entries.some(entry => entry.jobId === uncertainJobId)
          ? 'This request is confirmed in print history. Do not submit it again.'
          : 'This request is not yet visible in recent history. Its status remains uncertain; do not retry blindly.'}</p>}
        <p>{s.acknowledgedRequestCount} delivery acknowledged - {s.submittedRequestCount} sent to printer - {s.failedRequestCount} printer errors - {s.outcomeUnknownRequestCount} outcomes uncertain</p>
        <p>Sent to printer does not confirm physical output. Status counts may overlap.</p>
        <details><summary>Recent history</summary>
        <ul>{current.history.entries.map(entry => <li key={entry.jobId}>
          {entry.assignedAt ? new Date(entry.assignedAt).toLocaleString() : 'Time unavailable'}{' - '}
          {entry.printerId || 'Printer unavailable'}{' - '}
          {entry.requestedCopies ?? 'Unknown'} copies{' - '}{printStatus(entry)}
          {entry.deliveryAcknowledged ? ' - Delivery acknowledged' : ' - Awaiting delivery acknowledgement'}
        </li>)}</ul>
        {!current.history.entries.length && <p>No retained print requests.</p>}
        {current.history.nextCursor && <p>Showing the latest 20 requests. Totals include all retained requests.</p>}
        </details>
      </>}
  </section>;
}
